import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Building2, CheckCircle2, Globe2, Inbox, ListChecks, Scale, ShieldAlert, ShieldCheck, UserCheck, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, fmtUSD, fmtVal, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { CanvasTabs } from "@/components/meridian/CanvasTabs";
import { PolicyChangeRow } from "@/pages/Policy";

const SEV = { High: "text-red-700 border-red-200 bg-red-50", Medium: "text-amber-700 border-amber-200 bg-amber-50", Low: "text-slate-700 border-slate-200 bg-slate-50" };
const btn = "text-[11px] font-semibold flex items-center gap-1 px-3 py-1.5 rounded-full border bg-white transition-colors disabled:opacity-40";
const ping = () => window.dispatchEvent(new Event("omnifi:approvals"));

function useAct(reload) {
  const { refreshTasks } = useApp();
  return async (fn, msg) => {
    try { await fn(); toast.success(msg); } catch (e) { if (e.response?.status !== 403) toast.error(errMsg(e) || "Action failed"); }
    await reload(); ping(); refreshTasks();
  };
}

function Row({ id, children }) {
  return <div className="panel panel-hover p-5 flex flex-wrap gap-4 items-start rise" data-testid={id}>{children}</div>;
}

function Empty({ text }) {
  return (
    <div className="empty-state rise" data-testid="approvals-empty">
      <span className="w-12 h-12 rounded-2xl bg-emerald-50 ring-1 ring-emerald-100 flex items-center justify-center mb-3"><CheckCircle2 className="w-6 h-6 text-emerald-600" /></span>
      <div className="text-sm font-semibold text-slate-900">You're all caught up</div>
      <div className="text-xs text-slate-500 mt-1 max-w-sm">{text}</div>
    </div>
  );
}

function Loans({ items, act }) {
  const [why, setWhy] = useState({});
  if (!items.length) return <Empty text="No loans are waiting for a committee decision." />;
  const decide = (l, decision) => act(() => api.post(`/tasks/${l.id}/decision`, { decision, rationale: why[l.id] || "" }), `${l.title}: ${decision}`);
  return items.map((l) => (
    <Row key={l.id} id={`approval-loan-${l.id}`}>
      <div className="flex-1 min-w-[260px]">
        <Link to={`/task/${l.id}`} className="text-sm text-slate-900 hover:text-sky-700 font-medium">{l.title}</Link>
        <div className="text-xs text-slate-500 mt-0.5 num">Requested {fmtUSD(l.requested, true)} · LTV {fmtVal(l.ltv, "pct")} · DSCR {fmtVal(l.dscr, "x")}</div>
        <div className="text-xs mt-1">System: <span className="text-amber-700">{l.recommendation}</span>{l.recommended_amount ? ` at ${fmtUSD(l.recommended_amount, true)}` : ""}</div>
        {l.prepared_by_me && <div className="text-[11px] text-red-600 mt-1 flex items-center gap-1" data-testid={`approval-loan-sod-${l.id}`}><AlertTriangle className="w-3 h-3" />You prepared this file. Separation of duties blocks your decision.</div>}
      </div>
      <div className="w-full md:w-[420px] space-y-2">
        <input data-testid={`approval-loan-rationale-${l.id}`} value={why[l.id] || ""} onChange={(e) => setWhy({ ...why, [l.id]: e.target.value })} placeholder="Decision rationale (required)…" className="w-full text-xs border border-slate-300 rounded px-2 py-1.5 outline-none focus:border-sky-500" />
        <div className="flex flex-wrap gap-1.5 justify-end">
          {[["Approve", "border-emerald-500/50 text-emerald-700 hover:bg-emerald-500/5"], ["Approve with conditions", "border-sky-500/50 text-sky-700 hover:bg-sky-500/5"], ["Decline", "border-red-500/50 text-red-700 hover:bg-red-500/5"]].map(([d, c]) => (
            <button key={d} data-testid={`approval-loan-${d.toLowerCase().replace(/\s+/g, "-")}-${l.id}`} disabled={l.prepared_by_me || !(why[l.id] || "").trim()} onClick={() => decide(l, d)} className={`${btn} ${c}`}>{d}</button>
          ))}
        </div>
      </div>
    </Row>
  ));
}

function Gaps({ items, act }) {
  if (!items.length) return <Empty text="No compliance gaps are pending sign-off." />;
  const review = (g, status) => act(() => api.put(`/compliance/gaps/${g.id}/review`, { status, comment: "Reviewed from approvals inbox" }), `${g.id} ${status.toLowerCase()}`);
  return items.map((g) => (
    <Row key={g.id} id={`approval-gap-${g.id}`}>
      <span className={`chip ${SEV[g.severity] || SEV.Medium}`}>{g.severity}</span>
      <div className="flex-1 min-w-[260px]"><div className="text-sm text-slate-900"><span className="num text-slate-500 mr-1.5">{g.id}</span>{g.title}</div>
        <div className="text-xs text-slate-500 mt-0.5">US vs {g.jurisdiction} · {g.topic}</div>
        {g.recommended_action && <div className="text-[11px] text-slate-600 mt-1">{g.recommended_action}</div>}</div>
      <div className="flex gap-1.5">
        <button data-testid={`approval-gap-accept-${g.id}`} onClick={() => review(g, "Accepted")} className={`${btn} border-emerald-500/50 text-emerald-700`}><CheckCircle2 className="w-3 h-3" />Accept</button>
        <button data-testid={`approval-gap-reject-${g.id}`} onClick={() => review(g, "Rejected")} className={`${btn} border-red-500/50 text-red-700`}><XCircle className="w-3 h-3" />Reject</button>
      </div>
    </Row>
  ));
}

function Grc({ items, act }) {
  if (!items.length) return <Empty text="No GRC issues are ready to close or overdue." />;
  return items.map((i) => (
    <Row key={i.id} id={`approval-grc-${i.id}`}>
      <span className={`chip ${SEV[i.severity] || SEV.Medium}`}>{i.severity}</span>
      <div className="flex-1 min-w-[260px]"><div className="text-sm text-slate-900"><span className="num text-slate-500 mr-1.5">{i.id}</span>{i.title}</div>
        <div className="text-xs text-slate-500 mt-0.5">{i.owner} · due {i.due_date || "-"} · {i.progress.done}/{i.progress.total} steps</div>
        <div className="flex gap-1.5 mt-1">{i.ready && <span className="text-[10px] text-emerald-700" data-testid={`approval-grc-ready-${i.id}`}>All steps complete - ready to close</span>}{i.overdue && <span className="text-[10px] text-red-600" data-testid={`approval-grc-overdue-${i.id}`}>Overdue</span>}</div></div>
      <div className="flex gap-1.5">
        <Link to="/grc" className={`${btn} border-slate-300 text-slate-600`}>Review plan</Link>
        <button data-testid={`approval-grc-close-${i.id}`} onClick={() => act(() => api.put(`/grc/issues/${i.id}`, { status: "Closed", note: "Closed from approvals inbox" }), `${i.id} closed`)} className={`${btn} border-emerald-500/50 text-emerald-700`}><CheckCircle2 className="w-3 h-3" />Close issue</button>
      </div>
    </Row>
  ));
}

function Access({ items, act }) {
  if (!items.length) return <Empty text="All active users have a current access review." />;
  const cert = (p, decision) => act(() => api.post(`/grc/access-reviews/${p.persona}`, { decision }), `${p.name}: ${decision.toLowerCase()}`);
  return items.map((p) => (
    <Row key={p.persona} id={`approval-access-${p.persona}`}>
      <div className="w-52"><div className="text-sm text-slate-900">{p.name}{p.self && <span className="ml-1 text-[10px] text-sky-700">(you)</span>}</div><div className="text-[11px] text-slate-500">{p.role}</div></div>
      <div className="flex-1 min-w-[200px] flex flex-wrap gap-1">{p.permissions.map((x) => <span key={x} className="text-[10px] num px-1.5 py-0.5 rounded border border-slate-300 text-slate-600">{x}</span>)}</div>
      <div className="flex flex-col items-end gap-1">
        <div className="flex gap-1.5">
          <button data-testid={`approval-access-certify-${p.persona}`} onClick={() => cert(p, "Certified")} className={`${btn} border-emerald-500/50 text-emerald-700`}><ShieldCheck className="w-3 h-3" />Certify</button>
          <button data-testid={`approval-access-revoke-${p.persona}`} onClick={() => cert(p, "Revoke requested")} className={`${btn} border-red-500/50 text-red-700`}><XCircle className="w-3 h-3" />Request revoke</button>
        </div>
        {p.self && <span className="text-[10px] text-red-600">Self-certification is blocked (segregation of duties)</span>}
      </div>
    </Row>
  ));
}

function PolicyTab({ items, reload }) {
  const { personaId } = useApp();
  if (!items.length) return <Empty text="No credit policy changes are waiting for a second approval." />;
  return items.map((c) => <PolicyChangeRow key={c.id} c={c} me={personaId} reload={reload} canEdit />);
}

function AmlTab({ items }) {
  if (!items.length) return <Empty text="No AML case dispositions are waiting for approval." />;
  return items.map((c) => (
    <div key={c.id} className="panel p-4 flex flex-wrap justify-between gap-3" data-testid={`approval-aml-${c.id}`}>
      <div><div className="text-sm font-semibold text-slate-900">{c.id} · {c.typology}</div><div className="text-[11px] text-slate-500">Severity {c.severity} · {c.item_ids.join(", ")} · investigator {c.investigator}</div>
        <div className="text-xs text-slate-700 mt-1"><b>{c.disposition}</b> · {c.rationale}</div>{c.self && <div className="text-[10px] text-red-600">You investigated this case - another reviewer must approve (segregation of duties)</div>}</div>
      <a href={`/aml?tab=cases&case=${c.id}`} data-testid={`approval-aml-open-${c.id}`} className="btn-primary h-fit">Review case</a>
    </div>));
}

export default function Approvals() {
  const { personaId, personaLabel } = useApp();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("loans");
  const load = useCallback(() => api.get("/approvals").then((r) => setD(r.data)), []);
  useEffect(() => { load(); }, [load, personaId]);
  const act = useAct(load);
  if (!d) return <div className="flex-1 p-8 text-sm text-slate-500">Loading approvals…</div>;
  const c = d.counts;
  const View = { loans: Loans, gaps: Gaps, grc: Grc, access: Access, policy: PolicyTab, aml: AmlTab }[tab];
  return (
    <div className="flex flex-col flex-1 min-h-0 min-w-0" data-testid="approvals-page">
      <div className="px-6 py-5 border-b border-slate-200/80 bg-white flex flex-wrap items-end justify-between gap-3">
        <div><div className="eyebrow">Approval inbox</div><h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-3 mt-1"><span className="icon-chip"><Inbox className="w-5 h-5" /></span>Approvals</h1></div>
        <div className="flex flex-wrap gap-2 text-xs">
          {[["Loans", c.loans, Building2, "loans"], ["Gaps", c.gaps, Globe2, "gaps"], ["GRC", c.grc, ListChecks, "grc"], ["Access", c.access, UserCheck, "access"], ["Policy", c.policy, Scale, "policy"], ["AML", c.aml, ShieldAlert, "aml"]].map(([l, v, I, k]) => (
            <button key={k} onClick={() => setTab(k)} className={`text-left rounded-xl px-4 py-2 ring-1 ${tab === k ? "ring-sky-300 bg-sky-50" : "ring-slate-200 bg-white hover:ring-sky-200"}`}><div className="eyebrow flex items-center gap-1"><I className="w-3 h-3" />{l}</div><div className="num text-xl font-semibold text-slate-900" data-testid={`approvals-count-${k}`}>{v}</div></button>
          ))}
        </div>
      </div>
      <CanvasTabs tab={tab} setTab={setTab} tabs={[["loans", "Committee decisions", c.loans], ["gaps", "Gap sign-offs", c.gaps], ["grc", "GRC issues", c.grc], ["access", "Access reviews", c.access], ["policy", "Policy changes", c.policy], ["aml", "AML cases", c.aml]]} />
      <div className="text-[11px] text-slate-500 px-6 pt-3 flex items-center gap-1.5"><UserCheck className="w-3.5 h-3.5" />Acting as {personaLabel}.{!d.approver && " This role has no approval rights; actions will be blocked and logged."}</div>
      <div className="flex-1 min-h-0 overflow-y-auto thin-scroll grid-bg p-6 space-y-3"><View items={d[tab]} act={act} reload={load} /></div>
    </div>
  );
}
